/**
 * GameNightPage — Live Game Tracker (v2 redesign)
 *
 * Dark live win-probability hero (green mono %, actual scores), then
 * per-game cards: red LIVE clock, mono score line, and your players in
 * that game with ON COURT tags + live fantasy points. Opponent players
 * listed below. Data comes from the league live endpoints (preview mock
 * in dev until the live data service ships).
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, StatStrip, V2SectionHead } from '../components/JaiV2'
import PlayerPhoto from '../components/PlayerPhoto'
import { JAIMascot } from '../components/JAIMascot'
import TeamChip from '../components/TeamChip'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_LEAGUE = {
  id: 'preview',
  name: 'The Hardwood Cartel',
  teamName: 'Rim Reapers',
  season: 2025,
  syncStatus: 'success',
}

const PREVIEW_MY_SCORE   = 184.6
const PREVIEW_OPP_SCORE  = 171.2
const PREVIEW_WIN_PROB   = 72
const PREVIEW_OPPONENT   = 'Dunks & Dimes'

const PREVIEW_GAMES = [
  {
    id: 'g1',
    homeTeam: 'LAL', awayTeam: 'GSW',
    homeScore: 98, awayScore: 91,
    quarter: 3, clock: '4:22',
    status: 'live', // 'pre' | 'live' | 'final'
  },
  {
    id: 'g2',
    homeTeam: 'BOS', awayTeam: 'MIA',
    homeScore: 112, awayScore: 109,
    quarter: 4, clock: '1:05',
    status: 'live',
  },
  {
    id: 'g3',
    homeTeam: 'DEN', awayTeam: 'PHX',
    homeScore: 0, awayScore: 0,
    quarter: null, clock: null,
    status: 'pre',
    tipoff: '8:30 PM ET',
  },
  {
    id: 'g4',
    homeTeam: 'NYK', awayTeam: 'CHI',
    homeScore: 119, awayScore: 104,
    quarter: null, clock: null,
    status: 'final',
  },
]

const PREVIEW_MY_PLAYERS = [
  {
    id: 'p1', name: 'LeBron James', pos: 'SF', team: 'LAL', gameId: 'g1',
    pts: 22, reb: 8, ast: 6, fantasyPts: 48.5, projectedPts: 52.0,
    status: 'live', playing: true,
  },
  {
    id: 'p2', name: 'Stephen Curry', pos: 'PG', team: 'GSW', gameId: 'g1',
    pts: 18, reb: 3, ast: 7, fantasyPts: 39.5, projectedPts: 58.0,
    status: 'live', playing: true,
  },
  {
    id: 'p3', name: 'Jayson Tatum', pos: 'SF', team: 'BOS', gameId: 'g2',
    pts: 31, reb: 9, ast: 4, fantasyPts: 62.0, projectedPts: 55.0,
    status: 'live', playing: true,
  },
  {
    id: 'p4', name: 'Nikola Jokić', pos: 'C', team: 'DEN', gameId: 'g3',
    pts: 0, reb: 0, ast: 0, fantasyPts: 0, projectedPts: 65.0,
    status: 'pre', playing: false,
  },
  {
    id: 'p5', name: 'Bam Adebayo', pos: 'C', team: 'MIA', gameId: 'g2',
    pts: 14, reb: 11, ast: 3, fantasyPts: 41.0, projectedPts: 44.0,
    status: 'live', playing: false, // on bench
  },
]

const PREVIEW_OPP_PLAYERS = [
  { id: 'o1', name: 'Kevin Durant',      pos: 'SF', team: 'PHX', gameId: 'g3', fantasyPts: 0,    projectedPts: 54.0, status: 'pre'   },
  { id: 'o2', name: 'Damian Lillard',    pos: 'PG', team: 'MIA', gameId: 'g2', fantasyPts: 50.0, projectedPts: 48.0, status: 'live'  },
  { id: 'o3', name: 'Anthony Davis',     pos: 'C',  team: 'LAL', gameId: 'g1', fantasyPts: 49.5, projectedPts: 50.0, status: 'live'  },
  { id: 'o4', name: 'Tyrese Haliburton', pos: 'PG', team: 'IND', gameId: 'g4', fantasyPts: 55.5, projectedPts: 52.0, status: 'final' },
]

// ── Main component ─────────────────────────────────────────────────────────────

export default function GameNightPage() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? PREVIEW_LEAGUE : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)

  const sport = (league?.sport || 'nba').toLowerCase()

  const myScore    = IS_PREVIEW ? PREVIEW_MY_SCORE    : 0
  const oppScore   = IS_PREVIEW ? PREVIEW_OPP_SCORE   : 0
  const winProb    = IS_PREVIEW ? PREVIEW_WIN_PROB    : 50
  const opponent   = IS_PREVIEW ? PREVIEW_OPPONENT    : 'Opponent'
  const myPlayers  = IS_PREVIEW ? PREVIEW_MY_PLAYERS  : []
  const oppPlayers = IS_PREVIEW ? PREVIEW_OPP_PLAYERS : []
  const games      = IS_PREVIEW ? PREVIEW_GAMES       : []

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  const liveCount   = games.filter(g => g.status === 'live').length
  const activeCount = myPlayers.filter(p => p.status === 'live').length
  const vsProj      = myPlayers.reduce((acc, p) => p.status === 'pre' ? acc : acc + (p.fantasyPts - p.projectedPts), 0)

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  const statCells = [
    [String(liveCount), 'Games live', true],
    [String(activeCount), 'Your players'],
    [`${vsProj >= 0 ? '+' : ''}${vsProj.toFixed(1)}`, 'vs proj'],
  ]

  return (
    <AppShell>
      <V2Page
        topBar={<ToolNav leagueId={id} />}
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Live` : 'Live'}
          </Link>
        }
        title="Game Night"
        sub={`${league?.teamName || 'Your team'} vs ${opponent} — live box scores, updated as games run.`}
        maxWidth={880}
        action={<StatStrip cells={statCells} />}
      >

        {/* ── Live win-prob hero ────────────────────────────────────────── */}
        <div style={{ background: V2.darkHero, borderRadius: 16, padding: '18px 24px', color: 'white', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.13em', color: 'rgba(255,255,255,0.45)', fontFamily: JAI_FONTS.mono, marginBottom: 5 }}>
              LIVE WIN PROBABILITY
            </div>
            <div style={{ fontSize: 38, fontWeight: 800, color: V2.successOnDark, fontFamily: JAI_FONTS.mono, letterSpacing: '-0.03em', lineHeight: 1 }}>
              {winProb}%
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
              <div style={{ width: `${winProb}%`, height: '100%', background: 'linear-gradient(90deg, #1E5BD6, #3A7CFF)' }} />
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: JAI_FONTS.mono }}>
              {myScore.toFixed(1)} <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span> {oppScore.toFixed(1)}
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)', fontFamily: JAI_FONTS.mono, marginTop: 3, textTransform: 'uppercase' }}>
              {(league?.teamName || 'You')} vs {opponent} · Actual
            </div>
          </div>
        </div>

        {/* ── Per-game cards with your players ──────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
          {games.map(g => {
            const yours = myPlayers.filter(p => p.gameId === g.id)
            return (
              <V2Card key={g.id} pad="16px 18px">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  {g.status === 'live' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: V2.error }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: V2.error, fontFamily: JAI_FONTS.mono, letterSpacing: '0.08em' }}>
                        Q{g.quarter} · {g.clock}
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: V2.faint, fontFamily: JAI_FONTS.mono, letterSpacing: '0.08em' }}>
                      {g.status === 'pre' ? `TIP ${g.tipoff}` : 'FINAL'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginBottom: yours.length ? 14 : 0, opacity: g.status === 'pre' ? 0.55 : 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: V2.navy }}>{g.awayTeam}</span>
                  <span style={{ fontSize: 26, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: V2.navy, letterSpacing: '-0.02em' }}>
                    {g.awayScore} <span style={{ color: 'rgba(10,31,68,0.25)' }}>–</span> {g.homeScore}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: V2.mute }}>{g.homeTeam}</span>
                </div>
                {yours.length > 0 && (
                  <div style={{ borderTop: '1px solid rgba(10,31,68,0.06)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {yours.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <PlayerPhoto name={p.name} size={26} rounded="full" />
                        <span style={{ fontSize: 13, fontWeight: 600, color: V2.navy, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                        <TeamChip abbr={p.team} sport={sport} />
                        {p.status === 'live' && p.playing && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: V2.success, padding: 0, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
                            ON COURT
                          </span>
                        )}
                        {p.status === 'live' && !p.playing && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: V2.faint, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>BENCH</span>
                        )}
                        <span style={{ fontSize: 14.5, fontWeight: 800, color: p.status === 'pre' ? V2.faint : V2.blue, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
                          {p.status === 'pre' ? `proj ${p.projectedPts.toFixed(0)}` : p.fantasyPts.toFixed(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </V2Card>
            )
          })}
        </div>

        {/* ── Their players tonight ─────────────────────────────────────── */}
        {oppPlayers.length > 0 && (
          <>
            <V2SectionHead>{opponent} tonight</V2SectionHead>
            <V2Card pad="0">
              {oppPlayers.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 20px', borderBottom: i < oppPlayers.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none' }}>
                  <PlayerPhoto name={p.name} size={28} rounded="full" />
                  <span style={{ fontSize: 13, fontWeight: 600, color: V2.navy, flex: 1, minWidth: 0 }}>{p.name}</span>
                  <span style={{ fontSize: 10.5, color: V2.faint, fontFamily: JAI_FONTS.mono }}>{p.pos}</span>
                  <TeamChip abbr={p.team} sport={sport} />
                  <span style={{ fontSize: 10.5, fontWeight: 700, fontFamily: JAI_FONTS.mono, letterSpacing: '0.06em', color: p.status === 'live' ? V2.error : V2.faint, width: 44, textAlign: 'right' }}>
                    {p.status === 'live' ? 'LIVE' : p.status === 'pre' ? 'PRE' : 'FINAL'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: p.status === 'pre' ? V2.faint : V2.navy, fontFamily: JAI_FONTS.mono, width: 56, textAlign: 'right' }}>
                    {p.status === 'pre' ? `p${p.projectedPts.toFixed(0)}` : p.fantasyPts.toFixed(1)}
                  </span>
                </div>
              ))}
            </V2Card>
          </>
        )}

      </V2Page>
    </AppShell>
  )
}
